import { c as _c } from "react-compiler-runtime";
import { createContext, createElement, useContext } from 'react';

/**
 * Constraints a layout manager enforces on a participating child — how that
 * child sits in the parent's flex/grid context.
 *
 * Intentionally empty for now: the context transport and subtree-reset plumbing
 * ship first, and the concrete constraint properties (flex-child, grid-child,
 * sizing, …) are added alongside the layout components that provide them. See
 * `constraintsToStyles` for the single extension point.
 */

/**
 * The value transported by `LayoutConstraintsContext`: a single
 * `LayoutConstraints` object applied uniformly to every participating child, or
 * `null` to clear inherited constraints.
 *
 * TODO: add a keyed mode that targets children individually by `layoutId`.
 * Encode it with an explicit discriminator (e.g. a tagged shape) rather than
 * inferring uniform-vs-keyed from the runtime shape of constraint fields — a
 * uniform value whose fields are object-valued (e.g. a StyleX conditional like
 * `{ default, ':hover' }`) must not be mistaken for a keyed map. To be designed
 * alongside the layout components that provide constraints.
 */

export const LayoutConstraintsContext = /*#__PURE__*/createContext(null);

// Converts resolved constraints into `StyleXStyles`.
//
// No-op for now: `LayoutConstraints` carries no properties yet, so even a
// resolved object produces no styles. The concrete constraint → StyleX mapping
// is added here alongside the layout components that provide constraints; until
// then `constraintStyles` is inert so components can wire it in without a later
// change to their render output.
function constraintsToStyles(constraints) {
  if (!constraints) {
    return undefined;
  }
  return undefined;
}
/**
 * Lets a component participate in a parent layout manager's constraints while
 * guaranteeing the subtree reset that keeps those constraints from cascading.
 *
 * A component consumes constraints for itself (`layoutConstraintStyles`) and
 * renders the returned children — which reset the context to `null` for the
 * subtree — in a single call, so the reset cannot be forgotten. The two returned
 * values name the consume side and the reset side explicitly:
 *
 * ```tsx
 * const { layoutConstraintStyles, layoutConstraintResetChildren } =
 *   useLayoutConstraints({ layoutId, children: props.children });
 * return (
 *   <div {...stylex.props(rootStyles, layoutConstraintStyles, xstyle)}>
 *     {layoutConstraintResetChildren}
 *   </div>
 * );
 * ```
 *
 * React context reaches every descendant regardless of depth, so without the
 * reset a layout manager's constraints would cascade into every nested
 * participant, not just its intended child. Components that only need to stop
 * that cascade (and never consume constraints themselves) should use
 * `LayoutConstraintsReset` instead.
 *
 * @param params.layoutId - Reserved for keyed mode; currently unused.
 * @param params.children - The component's `children`, returned reset.
 */
export function useLayoutConstraints(t0) {
  const $ = _c(7);
  const {
    children
  } = t0;
  const value = useContext(LayoutConstraintsContext);
  let t1;
  if ($[0] !== value) {
    t1 = constraintsToStyles(value);
    $[0] = value;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const layoutConstraintStyles = t1;
  let t2;
  if ($[2] !== children) {
    t2 = /*#__PURE__*/createElement(LayoutConstraintsContext.Provider, {
      value: null
    }, children);
    $[2] = children;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== layoutConstraintStyles || $[5] !== t2) {
    t3 = {
      layoutConstraintStyles,
      layoutConstraintResetChildren: t2
    };
    $[4] = layoutConstraintStyles;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
}

/**
 * Resets `LayoutConstraintsContext` to `null` for its subtree, capping a layout
 * manager's constraints at the outermost participant on each branch.
 *
 * Use this in any component that renders free-form consumer `children` but does
 * not consume constraints itself (e.g. `Dialog`, `Accordion`, `Drawer`). It is
 * the reset half of `useLayoutConstraints`, exposed on its own for containers
 * that only need to stop the cascade.
 */
export function LayoutConstraintsReset({
  children
}) {
  return /*#__PURE__*/createElement(LayoutConstraintsContext.Provider, {
    value: null
  }, children);
}